import StoryblokClient from 'storyblok-js-client'
import { StoryblokServerComponent, storyblokEditable } from '@storyblok/react/rsc'
import Grid from '@audioeye-marketing/ui/src/components/Grid'
import Stack from '@audioeye-marketing/ui/src/components/Stack'
import Box from '@audioeye-marketing/ui/src/components/Box'
import FeaturedArticle from '@/components/misc/FeaturedArticle'

const Storyblok = new StoryblokClient({
  accessToken: process.env.WWW_STORYBLOK_API_TOKEN,
  region: 'us',
})

const FeaturedArticles = async ({ blok, rels, backgroundColor }) => {
  const { data } = await Storyblok.get(`cdn/stories`, {
    version: process.env.NEXT_PUBLIC_WWW_APP_ENV === 'production' ? 'published' : 'draft',
    content_type: blok?.article_type || 'Post',
    per_page: blok?.number_of_articles || 3,
    sort_by: blok?.article_type === 'Press' ? 'content.published_date:desc' : 'content.last_updated_date:desc',
    starts_with: 'post',
  })

  const spacing = 4
  const columns = blok?.number_of_articles === '5' ? 2 : 3
  const baseGridProps = { xs: 12, sm: 6, md: 12 / columns }

  return (
    <Stack spacing={4}>
      <Grid
        {...storyblokEditable(blok)}
        container
        component="ul"
        spacing={spacing}
        justifyContent={blok?.justify_content}
        alignItems="stretch"
        // Negative margin to offset the spacing of the grid items and align to text content
        sx={{
          listStyleType: 'none', // Remove bullets from ul
          paddingLeft: '0', // Remove padding from ul
          marginLeft: `-${spacing * 4}px !important`,
          marginRight: `-${spacing * 4}px !important`,
        }}
      >
        {blok?.number_of_articles === '4' ? (
          // Layout for 4 articles (3-column grid, last column stacks 2 items)
          <>
            {data.stories.slice(0, 2).map((story) => (
              <Grid component="li" key={story?.uuid} {...baseGridProps} display="flex" justifyContent="center">
                <FeaturedArticle data={story} size="md" />
              </Grid>
            ))}
            <Grid
              component="li"
              key="last-column"
              {...baseGridProps}
              display="flex"
              flexDirection="column"
              gap={spacing}
            >
              {data.stories.slice(2, 4).map((story) => (
                <FeaturedArticle key={story?.uuid} data={story} size="sm" />
              ))}
            </Grid>
          </>
        ) : blok?.number_of_articles === '5' ? (
          // Layout for 5 articles (2-column grid, first item alone, rest stacked)
          <>
            <Grid component="li" key={data.stories[0]?.uuid} {...baseGridProps} display="flex" justifyContent="center">
              <FeaturedArticle data={data.stories[0]} size="lg" />
            </Grid>
            <Grid
              component="li"
              key="stacked-column"
              {...baseGridProps}
              display="flex"
              flexDirection="column"
              gap={spacing}
            >
              {data.stories.slice(1, 5).map((story) => (
                <FeaturedArticle key={story?.uuid} data={story} size="sm" />
              ))}
            </Grid>
          </>
        ) : (
          // Default layout for 3 articles (evenly spaced in a 3-column grid)
          data.stories.map((story) => (
            <Grid component="li" key={story?.uuid} {...baseGridProps} display="flex" justifyContent="center">
              <FeaturedArticle data={story} size="md" />
            </Grid>
          ))
        )}
      </Grid>
      {blok?.cta?.map((currentCta) => (
        <Box
          key={blok?._uid}
          sx={{
            display: 'flex',
            width: '100%',
            alignItems: 'center',
          }}
        >
          <StoryblokServerComponent blok={currentCta} rels={rels} backgroundColor={backgroundColor} />
        </Box>
      ))}
    </Stack>
  )
}

export default FeaturedArticles
